/* eslint-disable class-methods-use-this */
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { findIndex, groupBy, flatten, intersection } from 'lodash';
import { MatPaginator } from '@angular/material/paginator';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { v4 as uuidv4 } from 'uuid';
import { CollectionSettings } from './fields-collection.models';
import { DesignerSchemaFieldI } from '../../models/designer-schema-field.model';
import { ConditionOperator } from '../../../constants/conditions_operator_picker';
import { TemplateNameDirective } from '../../../common/components/template-name.directive';
import { Schema } from '../../package.models';

interface RowStateOptions {
  isActive?: boolean;
  isError?: boolean;
  isDuplicateError?: boolean;
  isOutsideDuplicateError?: boolean;
}

export interface ErrorType {
  index: number;
  message?: string;
  type?: string;
  prop?: string;
}

const filterPredicate = (data: any, filter: string) => {
  const {
    name,
    alias,
    data_type,
    projected_name,
    left,
    operator,
    right,
    function_name,
    field_name,
    column_name,
  } = data;
  const dataStr = [
    name,
    alias,
    data_type,
    projected_name,
    left,
    operator,
    right,
    function_name,
    field_name,
    column_name,
  ]
    .filter(Boolean)
    .map((item) => item.toLowerCase())
    .join('');
  return dataStr.indexOf(filter) !== -1;
};

@Component({
  selector: 'xp-fields-collection',
  template: `
    <div class="fields-collection-wrapper">
      <div
        class="fields-collection-header"
        *ngIf="
          !collectionSettings.hideSearch || (collectionSettings.autoFillFns && collectionSettings.autoFillFns.length)
        "
      >
        <div class="fields-collection-search" *ngIf="!collectionSettings.hideSearch">
          <input
            class="fields-collection-search-input form-control input-sm"
            (keyup)="applyFilter($event)"
            placeholder="Find"
            #input
          />
        </div>
        <div class="fields-collection-autofill">
          <div
            class="btn-group pull-right"
            *ngIf="collectionSettings.autoFillFns && collectionSettings.autoFillFns.length > 1"
          >
            <button type="button" class="btn btn-default btn-sm" (click)="autoFill()">Auto-fill</button>
            <button
              type="button"
              class="btn btn-default btn-sm dropdown-toggle xp-dropdown"
              [matMenuTriggerFor]="dropdown"
            >
              <span class="caret"></span>
              <span class="sr-only">Toggle Dropdown</span>
            </button>
            <mat-menu #dropdown="matMenu">
              <li
                mat-menu-item
                *ngFor="let autoFillFn of collectionSettings.autoFillFns"
                (click)="autoFill(autoFillFn.func)"
              >
                {{ autoFillFn.text }}
              </li>
            </mat-menu>
          </div>
          <button
            type="button"
            class="btn btn-default btn-sm pull-right"
            *ngIf="collectionSettings.autoFillFns && collectionSettings.autoFillFns.length === 1"
            (click)="autoFill()"
          >
            {{ collectionSettings.autoFillFns[0].text }}
          </button>
        </div>
      </div>

      <table
        mat-table
        [dataSource]="dataSource"
        [trackBy]="trackById"
        #table
        cdkDropList
        [cdkDropListData]="dataSource"
        (cdkDropListDropped)="onDragAndDropEvent($event)"
      >
        <ng-container matColumnDef="index">
          <th mat-header-cell *matHeaderCellDef class="constant-width"></th>
          <td mat-cell *matCellDef="let row; let i = index" class="constant-width fields-collection-row-index">
            {{ i + 1 + paginator.pageIndex * paginator.pageSize }}
          </td>
        </ng-container>

        <ng-container matColumnDef="position">
          <th mat-header-cell *matHeaderCellDef class="constant-width"></th>
          <td mat-cell *matCellDef="let element" class="constant-width">
            <div
              class="fields-collection-row-sort"
              cdkDragHandle
              (touchstart)="dragDisabled = false"
              (touchend)="dragDisabled = true"
              (mousedown)="dragDisabled = false"
              (mouseup)="dragDisabled = true"
              *ngIf="!collectionSettings.hideSortable"
            >
              <div class="editor-button">
                <i class="fa fa-arrows-v"></i>
              </div>
            </div>
          </td>
        </ng-container>

        <ng-container *ngFor="let column of columns; let i = index" [matColumnDef]="column">
          <th mat-header-cell *matHeaderCellDef [resizeColumn]="i !== columns.length - 1" [index]="i + 2">
            <ng-template [ngTemplateOutlet]="cellTemplates[column + '-header']"></ng-template>
          </th>
          <td mat-cell *matCellDef="let row; let index = index" [ngClass]="column + '-cell'">
            <ng-template
              [ngTemplateOutlet]="cellTemplates[column]"
              [ngTemplateOutletContext]="{
                $implicit: {
                  record: row,
                  index: index + paginator.pageIndex * paginator.pageSize,
                  focusedProp: row.focusedProp
                }
              }"
            ></ng-template>
          </td>
        </ng-container>

        <ng-container matColumnDef="add">
          <th mat-header-cell *matHeaderCellDef class="constant-width"></th>
          <td mat-cell *matCellDef="let row; let index = index" class="constant-width">
            <div class="fields-collection-row-add">
              <div class="editor-button" (click)="addRecord(index + paginator.pageIndex * paginator.pageSize + 1)">
                <i class="fa fa-plus"></i>
              </div>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="remove">
          <th mat-header-cell *matHeaderCellDef class="constant-width"></th>
          <td mat-cell *matCellDef="let row; let index = index" class="constant-width">
            <div class="fields-collection-row-remove">
              <div
                class="editor-button"
                (click)="removeRecord(index + paginator.pageIndex * paginator.pageSize)"
                [ngClass]="{
                  disabled: (records.length === 1 && !collectionSettings.allowEmptyCollection) || row.required
                }"
              >
                <i class="fa fa-remove"></i>
              </div>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr
          class="fields-collection-row"
          mat-row
          *matRowDef="let row; let i = index; columns: displayedColumns"
          cdkDrag
          [cdkDragData]="row"
          [cdkDragDisabled]="dragDisabled"
          (cdkDragReleased)="dragDisabled = true"
          [ngClass]="getRowState(i)"
        ></tr>

        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell no-items-found-text" colspan="4">No matches found</td>
        </tr>
      </table>

      <mat-paginator
        #paginator
        [hidden]="records.length <= collectionSettings.itemsPerPage"
        [pageSizeOptions]="[10, 20, 50]"
        [pageSize]="collectionSettings.itemsPerPage"
        [showFirstLastButtons]="true"
      ></mat-paginator>
    </div>
    <xp-fields-collection-errors-box
      [errors]="errors"
      class="fields-collection-errors"
    ></xp-fields-collection-errors-box>
  `,
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XpFieldsCollectionComponent implements OnInit, AfterContentInit, OnChanges {
  @Input() records: DesignerSchemaFieldI[];
  @Input() collectionSettings: Partial<CollectionSettings>;
  @Input() isValid: boolean;
  @Input() columns: string[];
  @Input() duplicationValidationProp: string;
  @Input() duplicationValidationPropName: string;
  @Output() recordsChange = new EventEmitter();
  @Output() save = new EventEmitter();
  @Output() validityChange = new EventEmitter();
  @ContentChildren(TemplateNameDirective) templates: QueryList<TemplateNameDirective>;
  @ViewChild(MatPaginator, { static: false })
  set paginator(value: MatPaginator) {
    if (this.dataSource) {
      this.dataSource.paginator = value;
      this.dataSource.data = this.records;
    }
  }
  @ViewChild('table') table: MatTable<any>;

  displayedColumns: string[] = ['index'];
  dataSource: MatTableDataSource<any>;

  dragDisabled = true;
  errors: ErrorType[] = [];
  find = '';
  rows: any[];
  cellTemplates = {};
  rowStates: RowStateOptions[] = [];

  ngAfterContentInit() {
    const templatesArray = this.templates.toArray();
    this.cellTemplates = templatesArray.reduce((acc, curr) => ({ ...acc, [curr.templateName]: curr.template }), {});
    this.displayedColumns = ['index', 'position', ...this.columns, 'add', 'remove'];

    if (this.collectionSettings.hideIndex) {
      this.displayedColumns = this.displayedColumns.filter((key) => key !== 'index');
    }
  }

  ngOnInit() {
    this.dataSource = new MatTableDataSource([]);

    if (!this.collectionSettings.allowEmptyCollection && !this.records.length) {
      this.addRecord(0, true);
    }

    this.checkCollectionValidity();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.records && changes.records.currentValue && this.dataSource) {
      const { filter } = this.dataSource;
      this.dataSource.data = this.records;

      if (filter) {
        this.applyFilter({ target: { value: filter } } as any);
      }

      setTimeout(() => {
        this.checkCollectionValidity();
      });
    }

    if (changes.collectionSettings && !changes.collectionSettings.firstChange) {
      const currentSettings: CollectionSettings = changes.collectionSettings.currentValue;

      if (currentSettings.outsideSourceValidation && currentSettings.outsideSourceValidation.unique) {
        this.validateDuplicates();
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  trackById(index: number, item: DesignerSchemaFieldI) {
    return item.id;
  }

  onDragAndDropEvent(event: CdkDragDrop<MatTableDataSource<any>, any>) {
    const prevIndex = this.records.findIndex((d) => d === event.item.data);
    const toIndex = this.dataSource.paginator
      ? event.currentIndex + this.dataSource.paginator.pageIndex * this.dataSource.paginator.pageSize
      : event.currentIndex;
    moveItemInArray(this.records, prevIndex, toIndex);
    const { filter } = this.dataSource;
    this.dataSource.data = this.records;

    if (filter) {
      this.applyFilter({ target: { value: filter } } as any);
    }
    this.onChange(this.records);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filterPredicate = filterPredicate;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  getRowState(index: number): any {
    if (this.dataSource) {
      if (this.dataSource.paginator) {
        const rowState =
          this.rowStates[index + this.dataSource.paginator.pageIndex * this.dataSource.paginator.pageSize] || {};
        return {
          active: rowState.isActive,
          error: rowState.isError || rowState.isDuplicateError,
          duplicateError: rowState.isDuplicateError,
        };
      }

      const rowState = this.rowStates[index] || {};
      return {
        active: rowState.isActive,
        error: rowState.isError || rowState.isDuplicateError,
        duplicateError: rowState.isDuplicateError,
      };
    }
    return {};
  }

  addRecord(index: number, isInitial?: boolean) {
    const realIndex = this.records.findIndex((item) => item.id === this.dataSource.filteredData[index - 1].id) + 1;
    let record;

    if (typeof this.collectionSettings.addRecord === 'function') {
      this.collectionSettings.addRecord(realIndex);
    } else {
      if (!this.collectionSettings.emptyRecord) {
        this.collectionSettings.emptyRecord = {
          alias: '',
          name: '',
          FC_pristine: true,
        };
      }
      record = { ...this.collectionSettings.emptyRecord, id: uuidv4() };
      this.records.splice(realIndex, 0, record);

      const { filter } = this.dataSource || {};
      this.dataSource.data = this.records;

      if (filter) {
        this.applyFilter({ target: { value: filter } } as any);
      }
      this.onChange(this.records, isInitial);
    }
  }

  removeRecord(index: number) {
    const realIndex = this.records.findIndex((item) => item.id === this.dataSource.filteredData[index].id);
    this.removeError(index);

    this.rowStates.splice(realIndex, 1);
    this.records = this.records.filter((item, i) => i !== realIndex);

    this.validateDuplicates();

    const { filter } = this.dataSource;
    this.dataSource.data = this.records;

    if (filter) {
      this.applyFilter({ target: { value: filter } } as any);
    }
    this.onChange(this.records);
  }

  onChange(records: any[], isInitial?: boolean) {
    this.recordsChange.emit({ records: records || this.records, isInitial });
  }

  autoFill(autofillFn?: () => any) {
    let autoFillFunction = autofillFn;

    if (!autofillFn && this.collectionSettings.autoFillFns && this.collectionSettings.autoFillFns.length) {
      autoFillFunction = this.collectionSettings.autoFillFns[0].func;
    }

    if (typeof autoFillFunction === 'function' && this.collectionSettings.parentSchemas) {
      this.records = autoFillFunction();
      const { filter } = this.dataSource;
      this.dataSource.data = this.records;

      if (filter) {
        this.applyFilter({ target: { value: filter } } as any);
      }
      this.onChange(this.records);
    }
  }

  autoGenerateAll() {
    this.records.forEach((record, index) => {
      this.autoGenerate(index);
    });
    this.checkCollectionValidity();
  }

  autoGenerate(recordsIndex: number) {
    if (typeof this.collectionSettings.autoGenerateFn === 'function' && this.records[recordsIndex]) {
      this.records[recordsIndex].FC_pristine = false;
      this.collectionSettings.autoGenerateFn(this.records[recordsIndex]);
    }
    this.checkCollectionValidity();
  }

  setRecordPristineState(index: number, isPristine: boolean) {
    if (this.records[index].FC_pristine !== isPristine) {
      this.records[index] = { ...this.records[index], FC_pristine: isPristine };
      this.onChange(this.records);
    }
  }

  isRecordPristine(index: number): boolean {
    if (!this.records[index]) {
      return true;
    }

    return this.records[index].FC_pristine;
  }

  setRowState(index: number, options: RowStateOptions) {
    this.rowStates[index] = { ...(this.rowStates[index] || {}), ...options };

    if (!options.isActive && !this.dataSource.filter) {
      this.records = this.records.map((item) => ({ ...item, isFocused: false }));
      const { filter } = this.dataSource;

      if (filter) {
        this.applyFilter({ target: { value: filter } } as any);
      }
    }
  }

  validateDuplicates() {
    if (!this.duplicationValidationProp) {
      return;
    }

    const duplicationMap = groupBy(this.records, (record) => record[this.duplicationValidationProp]);
    let outsideErrors = [];

    if (this.collectionSettings.outsideSourceValidation && this.collectionSettings.outsideSourceValidation.unique) {
      const duplicationMapForArrays = flatten(Object.keys(duplicationMap).map((item) => item.split(',')));
      const outsideErrorsMap = this.collectionSettings.outsideSourceValidation.unique.validation.reduce(
        (acc, value) => ({
          ...acc,
          ...(duplicationMap[value] || duplicationMapForArrays.includes(value) ? { [value]: true } : {}),
        }),
        {},
      );

      outsideErrors = this.records
        .filter(
          (item) =>
            outsideErrorsMap[item[this.duplicationValidationProp]] ||
            intersection(item[this.duplicationValidationProp], Object.keys(outsideErrorsMap)).length,
        )
        .map((record) => ({
          index: this.records.indexOf(record),
          prop: this.duplicationValidationProp,
        }));
    }

    const errors: Array<ErrorType[]> = Object.keys(duplicationMap)
      .filter((key) => key && duplicationMap[key].length > 1)
      .map((key) =>
        duplicationMap[key].map((record) => ({
          index: this.records.indexOf(record),
          prop: this.duplicationValidationProp,
        })),
      );

    this.records.forEach((record, index) => {
      if (
        this.records[index] &&
        (this.records[index].isDuplicateError || this.records[index].isOutsideDuplicateError)
      ) {
        this.setRowState(index, { isDuplicateError: false, isOutsideDuplicateError: false });
        this.records[index] = { ...this.records[index], isDuplicateError: false, isOutsideDuplicateError: false };
      }
    });

    flatten(errors).forEach(({ index }) => {
      this.setRowState(index, { isDuplicateError: true });
      this.records[index] = { ...this.records[index], isDuplicateError: true };
    });

    outsideErrors.forEach(({ index }) => {
      this.setRowState(index, { isOutsideDuplicateError: true });
      this.records[index] = { ...this.records[index], isOutsideDuplicateError: true };
    });

    if (this.dataSource.paginator) {
      this.dataSource.data = [...this.records];
    }
  }

  isDuplicateError(index: number) {
    return (this.rowStates[index] || {}).isDuplicateError;
  }

  sortErrors() {
    this.errors.sort((a, b) => {
      if (a.index > b.index) return 1;
      if (a.index < b.index) return -1;
      return 0;
    });
  }

  updateRowStateError(index: number) {
    const errorExists = !!this.errors.find((error) => error.index === index);

    if (this.rowStates[index] && errorExists !== this.rowStates[index].isError) {
      this.setRowState(index, {
        isError: errorExists,
        isDuplicateError: errorExists,
        isOutsideDuplicateError: errorExists,
      });
    }
  }

  registerError(index: number, message: string, prop: string): ErrorType {
    let error = this.errors.find((item) => item.index === index && item.prop === prop);
    if (!error) {
      error = {
        index,
        message,
        prop,
      };
      this.errors = [...this.errors, error];
      this.sortErrors();
    } else if (error.message !== message) {
      error.message = message;
    }
    this.updateRowStateError(index);
    return error;
  }

  removeError(index: number, prop?: string) {
    const realIndex = this.records.findIndex((item) => item.id === this.dataSource.filteredData[index].id);
    let errorsIndexes: any[] = this.errors.map((item, indexInArray) => ({
      shouldRemove: item.index === realIndex,
      indexInArray,
    }));

    if (prop) {
      errorsIndexes = this.errors.map((item, indexInArray) => ({
        shouldRemove: item.index === realIndex && item.prop === prop,
        indexInArray,
      }));
    }

    errorsIndexes = errorsIndexes.filter((item) => item.shouldRemove).map((item) => item.indexInArray);

    if (errorsIndexes.length) {
      this.errors = this.errors.filter((item, indexInArray) => !errorsIndexes.includes(indexInArray));
      this.sortErrors();
    }
    this.updateRowStateError(realIndex);
  }

  checkCollectionValidity() {
    this.validateDuplicates();
    const valid = this.errors.length === 0;
    this.validityChange.emit(valid);
  }

  focusError(error: ErrorType) {
    this.dataSource.filter = '';
    this.focusOnInput(error.index, error.prop);
  }

  focusOnInput(matchedIndex: number, prop: string) {
    const realIndex = this.records.findIndex(
      (item) => item.id === (this.dataSource.filteredData[matchedIndex] || {}).id,
    );

    if (realIndex === -1) {
      return;
    }

    if (this.dataSource.paginator) {
      this.dataSource.paginator.pageIndex = Math.floor(realIndex / this.dataSource.paginator.pageSize);
    }

    this.records = this.records.map((item, index) => ({ ...item, focusedProp: index === realIndex ? prop : null }));
    const { filter } = this.dataSource;
    this.dataSource.data = this.records;

    if (filter) {
      this.applyFilter({ target: { value: filter } } as any);
    }
  }
}
